import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Phone, Menu, X, FileText } from 'lucide-react';

const Header = ({ isScrolled }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { href: '#services', label: 'Services' },
    { href: '#pricing', label: 'Pricing' },
    { href: '#faq', label: 'FAQ' },
    { href: '#contact', label: 'Contact' },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setMobileMenuOpen(false);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-md' 
          : 'bg-white/80 backdrop-blur-sm'
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
        <div className="flex items-center justify-between h-20 lg:h-24">
          {/* Logo */}
          <a 
            href="#" 
            className="flex items-center"
            data-testid="header-logo"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_ld-plaster-preview/artifacts/7zjxvswd_flex-plastering-northampton.png" 
              alt="Flex Plastering Logo"
              className="h-28 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="text-slate-600 hover:text-red-600 font-medium text-sm transition-colors duration-200"
                data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              className="border-red-600 text-red-600 hover:bg-red-50 font-medium"
              onClick={(e) => scrollToSection(e, '#contact')}
              data-testid="header-quote-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Get a Quote
            </Button>
            <Button
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white font-medium shadow-[0_4px_14px_0_rgba(196,30,58,0.39)]"
              asChild
              data-testid="header-call-btn"
            >
              <a href="tel:07766111127">
                <Phone className="w-4 h-4 mr-2" />
                07766 111127
              </a>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-slate-600 hover:text-red-600 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div 
            className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-lg border-t border-stone-100 mobile-menu-enter"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col py-4 px-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="py-3 px-4 text-slate-600 hover:text-red-600 hover:bg-slate-50 rounded-lg font-medium transition-colors"
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  {link.label}
                </a>
              ))}
              <div className="flex flex-col gap-3 mt-4 pt-4 border-t border-slate-100">
                <Button
                  variant="outline"
                  className="w-full border-red-600 text-red-600 hover:bg-red-50 font-medium"
                  onClick={(e) => scrollToSection(e, '#contact')}
                  data-testid="mobile-quote-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Get a Free Quote
                </Button>
                <Button
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-medium"
                  asChild
                  data-testid="mobile-call-btn"
                >
                  <a href="tel:07766111127">
                    <Phone className="w-4 h-4 mr-2" />
                    Call 07766 111127
                  </a>
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
